import { useEffect, useState } from "react";
import { getBankingContractById } from "../../../../Rest_api/RestApi";
import { BankingContractDetail, BankingContractPeriodResponse } from "../../../../models/banking";

function fmt(val: string | number | null | undefined, suffix = ""): string {
  if (val === null || val === undefined || val === "") return "--";
  return `${val}${suffix}`;
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return "--";
  const [y, m, d] = val.split("-");
  if (!y || !m || !d) return val;
  return `${d}-${m}-${y}`;
}

function Badge({ count }: { count: number }) {
  return (
    <span className="ml-2 px-2 py-0.5 rounded-full bg-[#F2F2F5] text-[#6B6877] text-[11px] font-medium">
      {count}
    </span>
  );
}

function FieldCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-[#8C8A96]">{label}</span>
      <span className="text-[12px] font-medium text-[#1A1B2B]">{value}</span>
    </div>
  );
}

function PeriodRow({ period, isReturn }: { period: BankingContractPeriodResponse; isReturn?: boolean }) {
  const cols = isReturn
    ? [
        { label: "Start Date", value: fmtDate(period.start) },
        { label: "End Date", value: fmtDate(period.end) },
        { label: "Quantum", value: fmt(period.quantum_mw, " MW") },
        { label: "Banking Factor", value: fmt(period.banking_factor_pct, "%") },
        { label: "Start Time", value: fmt(period.from_time) },
        { label: "End Time", value: fmt(period.to_time) },
        { label: "Basis", value: period.basis ? period.basis.charAt(0).toUpperCase() + period.basis.slice(1) : "--" },
      ]
    : [
        { label: "Start Date", value: fmtDate(period.start) },
        { label: "End Date", value: fmtDate(period.end) },
        { label: "Quantum", value: fmt(period.quantum_mw, " MW") },
        { label: "Max Quantum", value: period.quantum_is_max ? "Yes" : "No" },
        { label: "Start Time", value: fmt(period.from_time) },
        { label: "End Time", value: fmt(period.to_time) },
        { label: "Basis", value: period.basis ? period.basis.charAt(0).toUpperCase() + period.basis.slice(1) : "--" },
      ];

  return (
    <div className="grid grid-cols-7 gap-x-3 gap-y-1 py-3 border-b border-[#ECEEF3] last:border-b-0">
      {cols.map((col) => (
        <div key={col.label} className="flex flex-col gap-0.5">
          <span className="text-[11px] text-[#8C8A96]">{col.label}</span>
          <span className="text-[12px] font-medium text-[#1A1B2B]">{col.value}</span>
        </div>
      ))}
    </div>
  );
}

export function DocumentsTab({ contractId }: { contractId: string }) {
  const [contract, setContract] = useState<BankingContractDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setContract(null);
    setError(null);
    setLoading(true);
    getBankingContractById(contractId)
      .then((data) => {
        if (cancelled) return;
        setContract(data);
        if (!data) setError("Contract not found.");
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load contract details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [contractId]);

  if (loading) {
    return (
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-4 py-8 flex items-center justify-center text-[13px] text-[#8C8A96] mb-3.5">
        Loading contract details...
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-4 py-8 flex items-center justify-center text-[13px] text-[#8C8A96] mb-3.5">
        {error ?? "No contract data available."}
      </div>
    );
  }

  const capturedDate = contract.created_at ? fmtDate(contract.created_at.split("T")[0]) : "--";

  return (
    <div className="flex flex-col gap-3 mb-3.5">
      {/* Contract Details header */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4">
        <div className="text-[14px] font-semibold text-[#1A1B2B]">Contract Details</div>
        <div className="text-[11px] text-[#8C8A96] mt-0.5">
          Captured on {capturedDate} · Read-only snapshot of the form submission
        </div>
      </div>

      {/* Contract basics */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4">
        <div className="text-[13px] font-semibold text-[#1A1B2B] mb-4">Contract basics</div>
        <div className="grid grid-cols-4 gap-x-6 gap-y-4">
          <FieldCell label="LoA Number" value={fmt(contract.loa_number)} />
          <FieldCell label="LoA Date" value={fmtDate(contract.loa_date)} />
          <FieldCell label="Supplier" value={fmt(contract.supplier)} />
          <FieldCell label="Receiver" value={fmt(contract.receiver)} />
        </div>
        {contract.notes && (
          <div className="mt-4 border-t border-[#ECEEF3] pt-4">
            <span className="text-[11px] text-[#8C8A96] block mb-1">Notes</span>
            <span className="text-[12px] text-[#1A1B2B]">{contract.notes}</span>
          </div>
        )}
      </div>

      {/* Supply Periods */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4">
        <div className="flex items-center text-[13px] font-semibold text-[#1A1B2B] mb-3">
          Supply Periods
          <Badge count={contract.supply_periods.length} />
        </div>
        {contract.supply_periods.length === 0 ? (
          <div className="text-[12px] text-[#8C8A96]">No supply periods defined.</div>
        ) : (
          contract.supply_periods.map((p, i) => (
            <PeriodRow key={p.id ?? i} period={p} isReturn={false} />
          ))
        )}
      </div>

      {/* Return Periods */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4">
        <div className="flex items-center text-[13px] font-semibold text-[#1A1B2B] mb-3">
          Return Period
          <Badge count={contract.return_periods.length} />
        </div>
        {contract.return_periods.length === 0 ? (
          <div className="text-[12px] text-[#8C8A96]">No return periods defined.</div>
        ) : (
          contract.return_periods.map((p, i) => (
            <PeriodRow key={p.id ?? i} period={p} isReturn={true} />
          ))
        )}
      </div>
    </div>
  );
}

export default DocumentsTab;
